"use client";

import { useEffect, useRef, useState } from "react";
import {
  ChevronDown,
  LayoutDashboard,
  LogOut,
  Menu,
  Settings,
  UserRound,
  X,
} from "lucide-react";
import { MembershipCrest, membershipDisplayNames } from "@dj/profile-chrome/membership-crest";

import type { AnchorHTMLAttributes, ComponentType, ReactNode } from "react";
import type { ViewerChromeSession } from "@dj/profile-chrome/viewer-session";
import { initialsFromName, pastelAvatarColor } from "./avatarDisplay";

type MegaMenuSection = Readonly<{
  href: string;
  id: string;
  title: string;
  items: readonly {
    description?: string;
    href: string;
    label: string;
  }[];
}>;

type MegaMenuFooterCta = Readonly<{
  href: string;
  label: string;
}>;

export type MegaMenuItem = Readonly<{
  directLink?: boolean;
  compact?: boolean;
  footerCta?: MegaMenuFooterCta;
  subtitle?: string;
  badge?: string;
  openInNewTab?: boolean;
  href: string;
  id: string;
  introBody: string;
  introTitle: string;
  label: string;
  sections: readonly MegaMenuSection[];
}>;

export type SiteNavigationProps = Readonly<{
  viewerSession: ViewerChromeSession;
  locale: string;
  pathname: string;
  translate: (key: string) => string;
  navigation: { megaMenus: readonly MegaMenuItem[]; marketplaceMenus: readonly MegaMenuItem[] };
  LinkComponent: ComponentType<AnchorHTMLAttributes<HTMLAnchorElement> & { href: string }>;
  brand: ReactNode;
  brandHref?: string;
  languageSwitcher: ReactNode;
  appLinks: { dashboard: string; "my-profile": string; settings: string; login: string; signup: string };
  appLinksOpenInNewTab?: boolean;
  onLogout: () => void;
  marketplaceHref: (item: MegaMenuItem) => string | null;
  onNavigation?: (event: string, properties: { destination: string; label: string }) => void;
}>;

function internalHref(href: string): string {
  return /^(?:https?:\/\/|\/)/.test(href) ? href : `/${href}`;
}

function megaMenuWidthClassName(sectionCount: number): string {
  if (sectionCount <= 1) return "w-[min(calc(100vw-2rem),48rem)]";
  if (sectionCount === 2) return "w-[min(calc(100vw-2rem),60rem)]";
  if (sectionCount === 3) return "w-[min(calc(100vw-2rem),70rem)]";
  return "w-[min(calc(100vw-2rem),78rem)]";
}

export function SiteNavigation({
  viewerSession: chromeSession, locale, pathname, translate: t, navigation,
  LinkComponent: Link, brand, brandHref = "/", languageSwitcher, appLinks, onLogout,
  marketplaceHref, appLinksOpenInNewTab = false, onNavigation: track = () => {},
}: SiteNavigationProps) {
  const { megaMenus, marketplaceMenus } = navigation;
  const appLinkProps = appLinksOpenInNewTab ? {
    target: "_blank", rel: "noopener noreferrer",
    "aria-description": locale === "es" ? "Se abre en una pestaña nueva" : locale === "pt-BR" ? "Abre em uma nova aba" : "Opens in a new tab",
  } : {};
  const allMegaMenus = megaMenus;

  const isActiveHref = (href: string) => {
    const basePath = internalHref(href).split("#")[0];
    return pathname === basePath || pathname.startsWith(`${basePath}/`);
  };

  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeDropdownId, setActiveDropdownId] = useState<string | null>(null);
  const [mobileExpandedId, setMobileExpandedId] = useState<string | null>(null);
  const [isScrolled, setIsScrolled] = useState(false);
  const accountMenuRef = useRef<HTMLDivElement | null>(null);
  const navRef = useRef<HTMLElement | null>(null);
  const focusDropdownRef = useRef(false);
  const mobileToggleRef = useRef<HTMLButtonElement | null>(null);
  const mobilePanelRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!mobileMenuOpen) return;
    setActiveDropdownId(null);
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    mobileToggleRef.current?.focus();
    const closeDrawer = () => { setMobileMenuOpen(false); mobileToggleRef.current?.focus(); };
    const keydown = (event: KeyboardEvent) => {
      if (event.key === "Escape") { event.preventDefault(); closeDrawer(); return; }
      if (event.key !== "Tab") return;
      const controls = [mobileToggleRef.current, ...Array.from(navRef.current?.querySelectorAll<HTMLElement>('.site-header__language button, .site-header__language a, #site-mobile-navigation a[href], #site-mobile-navigation button:not([disabled]), #site-mobile-navigation [tabindex="0"]') ?? [])].filter((node): node is HTMLElement => Boolean(node));
      const first = controls[0], last = controls.at(-1);
      if (event.shiftKey && (document.activeElement === first || !controls.includes(document.activeElement as HTMLElement))) { event.preventDefault(); last?.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
    };
    const outside = (event: PointerEvent) => { if (!navRef.current?.contains(event.target as Node)) closeDrawer(); };
    const desktop = window.matchMedia("(min-width: 1100px)");
    const resize = () => { if (desktop.matches) closeDrawer(); };
    document.addEventListener("keydown", keydown);
    document.addEventListener("pointerdown", outside);
    desktop.addEventListener("change", resize);
    return () => { document.body.style.overflow = originalOverflow; document.removeEventListener("keydown", keydown); document.removeEventListener("pointerdown", outside); desktop.removeEventListener("change", resize); };
  }, [mobileMenuOpen]);
  useEffect(() => {
    if (!focusDropdownRef.current || !activeDropdownId) return;
    focusDropdownRef.current = false;
    navRef.current?.querySelector<HTMLAnchorElement>(`#nav-dropdown-${activeDropdownId.replace("mega:", "")} a`)?.focus();
  }, [activeDropdownId]);
  const closeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    if (!activeDropdownId) return undefined;

    const closeOnOutsidePointer = (event: PointerEvent) => {
      if (!navRef.current?.contains(event.target as Node)) {
        setActiveDropdownId(null);
      }
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        const trigger = navRef.current?.querySelector<HTMLButtonElement>(`[aria-controls="nav-dropdown-${activeDropdownId.replace("mega:", "")}"]`);
        trigger?.focus();
        setActiveDropdownId(null);
      }
    };

    document.addEventListener("pointerdown", closeOnOutsidePointer);
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.removeEventListener("pointerdown", closeOnOutsidePointer);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [activeDropdownId]);

  useEffect(() => (
    () => {
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
      }
    }
  ), []);

  const closeMobile = () => setMobileMenuOpen(false);
  const openDropdown = (id: string) => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
    setActiveDropdownId(id);
  };
  const closeDropdown = () => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
    setActiveDropdownId(null);
  };
  const scheduleDropdownClose = () => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
    }
    closeTimeoutRef.current = setTimeout(() => {
      closeTimeoutRef.current = null;
      setActiveDropdownId(null);
    }, 160);
  };

  const displayName = chromeSession.displayName || t("accountFallbackName");
  const fallbackInitials = initialsFromName(displayName);
  const fallbackAvatarColor = pastelAvatarColor(displayName);
  const membershipLabel = chromeSession.membershipType
    ? membershipDisplayNames[chromeSession.membershipType]
    : undefined;

  const accountMenuItems = [
    {
      href: appLinks["dashboard"],
      icon: LayoutDashboard,
      label: t("accountDashboard"),
    },
    {
      href: appLinks["my-profile"],
      icon: UserRound,
      label: t("accountMyProfile"),
    },
    {
      href: appLinks["settings"],
      icon: Settings,
      label: t("accountSettings"),
    },
  ] as const;

  const activeMegaMenu = allMegaMenus.find((item) => `mega:${item.id}` === activeDropdownId);
  const activeMegaMenuIsMarketplace = activeMegaMenu
    ? marketplaceMenus.some((item) => item.id === activeMegaMenu.id)
    : false;

  const renderInternalMenuLink = (
    href: string,
    label: string,
    className: string,
    onClick?: () => void,
    key?: string,
  ) => (
    <Link
      aria-current={isActiveHref(href) ? "page" : undefined}
      key={key ?? `${href}:${label}`}
      href={internalHref(href)}
      className={className}
      onClick={() => {
        track("public_navigation_click", { destination: href, label });
        onClick?.();
      }}
    >
      {label}
    </Link>
  );

  const renderMegaMenuLink = (
    item: MegaMenuSection["items"][number],
    className: string,
    key: string,
    onClick?: () => void,
  ) => (
    <Link
      aria-current={isActiveHref(item.href) ? "page" : undefined}
      className={className}
      href={internalHref(item.href)}
      key={key}
      onClick={() => {
        track("public_navigation_click", {
          destination: item.href,
          label: item.label,
        });
        closeDropdown();
        onClick?.();
      }}
    >
      <span>{item.label}</span>
      {item.description ? (
        <span className="site-nav-menu-description mt-1 text-xs font-medium leading-5 text-slate-500">
          {item.description}
        </span>
      ) : null}
    </Link>
  );

  const renderMarketplaceLabel = (item: MegaMenuItem) => item.subtitle ? (
    <span className="site-nav-featured-label" lang={locale}>
      <span className="site-nav-featured-title">{item.label}{item.badge ? <span className="site-nav-featured-badge">{item.badge}</span> : null}</span>
      <span className="site-nav-featured-subtitle">{item.subtitle}</span>
    </span>
  ) : <span className="site-nav-trigger-label" lang={locale}>{item.label}</span>;

  const renderTriggerContent = (label: string) => (
    <span className="site-nav-trigger-content">
      <span className="site-nav-trigger-label" lang={locale}>
        {label}
      </span>
      <ChevronDown aria-hidden="true" className="site-nav-trigger-chevron h-3.5 w-3.5 2xl:h-4 2xl:w-4" />
    </span>
  );

  const accountMenu = chromeSession.isAuthenticated ? (
    <div
      className="relative"
      ref={accountMenuRef}
      onMouseEnter={() => openDropdown("account")}
      onMouseLeave={scheduleDropdownClose}
    >
      <button
        type="button"
        className="inline-flex h-10 items-center gap-1 rounded-full bg-slate-50 px-2 text-slate-950 shadow-sm ring-1 ring-slate-200 transition hover:bg-white hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 2xl:h-11 2xl:gap-3 2xl:px-3 2xl:pr-4"
        aria-expanded={activeDropdownId === "account"}
        aria-haspopup="menu"
        aria-label={t("accountMenuLabel")}
        onClick={(event) => {
          if (event.detail === 0 && activeDropdownId === "account") {
            closeDropdown();
          } else {
            openDropdown("account");
          }
        }}
      >
        <span className="relative grid h-9 w-9 shrink-0 place-items-center">
          <span
            className="grid h-8 w-8 place-items-center overflow-hidden rounded-full text-sm font-bold text-slate-800 ring-2 ring-white"
            style={!chromeSession.avatarUrl ? { background: fallbackAvatarColor } : undefined}
            aria-hidden="true"
          >
            {chromeSession.avatarUrl ? (
              // Presentation-only cross-app avatar URL; it may come from Auth0 or a signed URL.
              // eslint-disable-next-line @next/next/no-img-element
              <img className="h-full w-full object-cover" src={chromeSession.avatarUrl} alt="" />
            ) : (
              fallbackInitials
            )}
          </span>
          {chromeSession.membershipType && membershipLabel ? (
            <MembershipCrest
              ariaLabel={`Active membership: ${membershipLabel}`}
              className="pointer-events-none absolute -right-1 -top-1 z-10 rounded-full border-2 border-white bg-white drop-shadow-sm"
              membershipType={chromeSession.membershipType}
              size="avatar-small"
            />
          ) : null}
        </span>
        <span className="hidden min-w-0 max-w-[9rem] text-left leading-tight 2xl:grid">
          <strong className="truncate text-sm font-bold">{displayName}</strong>
          {membershipLabel ? (
            <small className="truncate text-[0.68rem] font-semibold text-slate-500">
              {membershipLabel}
            </small>
          ) : null}
        </span>
        <ChevronDown
          aria-hidden="true"
          className={`h-4 w-4 shrink-0 text-slate-600 transition ${
            activeDropdownId === "account" ? "rotate-180" : ""
          }`}
        />
      </button>

      {activeDropdownId === "account" ? (
        <div
          role="menu"
          aria-label={t("accountMenuLabel")}
          className="absolute right-0 top-[calc(100%+0.6rem)] z-[90] w-56 overflow-hidden rounded-lg border border-slate-200 bg-white py-2 text-sm shadow-xl ring-1 ring-slate-200"
        >
          <div role="presentation" className="border-b border-slate-100 px-4 pb-3 pt-2">
            <strong className="block truncate text-sm font-bold text-slate-950">{displayName}</strong>
            {membershipLabel ? (
              <small className="mt-0.5 block truncate text-xs font-semibold text-slate-500">
                {membershipLabel}
              </small>
            ) : null}
          </div>
          {accountMenuItems.map(({ href, icon: Icon, label }) => (
            <a
              key={href}
              href={href}
              {...appLinkProps}
              role="menuitem"
              className="flex items-center gap-3 px-4 py-3 font-semibold text-slate-700 transition hover:bg-slate-50 hover:text-primary"
              onClick={closeDropdown}
            >
              <Icon aria-hidden="true" className="h-4 w-4 text-slate-500" />
              {label}
            </a>
          ))}
          <div className="my-1 border-t border-slate-100" />
          <button
            type="button"
            role="menuitem"
            className="flex w-full items-center gap-3 px-4 py-3 text-left font-semibold text-red-600 transition hover:bg-red-50"
            onClick={() => onLogout()}
          >
            <LogOut aria-hidden="true" className="h-4 w-4" />
            {t("accountLogout")}
          </button>
        </div>
      ) : null}
    </div>
  ) : null;

  return (
    <>
      <nav
        ref={navRef}
        role={mobileMenuOpen ? "dialog" : undefined}
        aria-modal={mobileMenuOpen ? "true" : undefined}
        aria-label={mobileMenuOpen ? t("openMenu") : undefined}
        className="site-header site-header--marketing"
      >
        <div className="site-header__shell">
          <div className="site-header__row">
            <div
              className={`site-header__bar ${isScrolled ? "site-header__bar--compact" : ""}`}
            >
            <div className="site-header__brand">
              <Link
                href={brandHref}
                className="site-header__brand-link logo-seesaw"
                onClick={closeMobile}
              >
                {brand}
              </Link>
            </div>

            <div className="site-header__nav site-header__nav--desktop">
              <div className="site-header__nav-group">
                {megaMenus.map((item) => item.directLink ? renderInternalMenuLink(item.href, item.label, "site-header__nav-link site-header__nav-link--platform rounded focus-visible:outline focus-visible:outline-2 focus-visible:outline-accent", closeDropdown, item.id) : (
                  <div
                    key={item.id}
                    className="site-header__menu-item"
                    onMouseEnter={() => openDropdown(`mega:${item.id}`)}
                    onMouseLeave={scheduleDropdownClose}
                  >
                    <button
                      type="button"
                      className={`site-header__nav-link site-header__nav-link--platform site-nav-trigger ${
                        isActiveHref(item.href) ? "site-header__nav-link--active" : ""
                      }`}
                      aria-current={isActiveHref(item.href) ? "page" : undefined}
                      aria-expanded={activeDropdownId === `mega:${item.id}`}
                      aria-controls={`nav-dropdown-${item.id}`}
                      onKeyDown={(event) => { if (event.key === "ArrowDown") { event.preventDefault(); if (activeDropdownId === `mega:${item.id}`) navRef.current?.querySelector<HTMLAnchorElement>(`#nav-dropdown-${item.id} a`)?.focus(); else { focusDropdownRef.current = true; openDropdown(`mega:${item.id}`); } } }}
                      onClick={(event) => {
                        if (event.detail === 0 && activeDropdownId === `mega:${item.id}`) {
                          closeDropdown();
                        } else {
                          openDropdown(`mega:${item.id}`);
                        }
                      }}
                    >
                      {renderTriggerContent(item.label)}
                    </button>
                  </div>
                ))}
              </div>

              <div className="site-header__divider" aria-hidden="true" />

              <div className="site-header__nav-group">
                {marketplaceMenus.map((item) => {
                  const className = `site-header__nav-link site-header__nav-link--marketplace site-nav-trigger ${item.id === "find-pilots-jobs" ? "site-nav-discovery-link" : ""} ${item.subtitle ? "site-nav-featured-link" : ""} ${
                    isActiveHref(item.href) && !Boolean(marketplaceHref(item))
                      ? "site-header__nav-link--active"
                      : ""
                  }`;
                  const onNavigate = () => {
                    track("public_navigation_click", {
                      destination: item.href,
                      label: item.label,
                    });
                    closeDropdown();
                  };
                  if (Boolean(marketplaceHref(item))) {
                    return (
                      <a
                        key={item.id}
                        href={marketplaceHref(item) || item.href}
                        target={appLinksOpenInNewTab || item.openInNewTab ? "_blank" : undefined}
                        className={className}
                        rel="noopener noreferrer"
                        onClick={onNavigate}
                      >
                        {renderMarketplaceLabel(item)}
                      </a>
                    );
                  }
                  return (
                    <Link
                      aria-current={isActiveHref(item.href) ? "page" : undefined}
                      key={item.id}
                      href={internalHref(item.href)}
                      className={className}
                      onClick={onNavigate}
                    >
                      <span className="site-nav-trigger-label" lang={locale}>{item.label}</span>
                    </Link>
                  );
                })}
              </div>

            </div>

            <div className="site-header__actions site-header__actions--desktop">
              {chromeSession.isAuthenticated ? (
                <div className="flex shrink-0 items-center">{accountMenu}</div>
              ) : (
                <>
                  <a
                    className="site-header__btn site-header__btn--login site-nav-action-trigger"
                    href={appLinks.login}
                    {...appLinkProps}
                  >
                    {t("login")}
                  </a>

                  <a
                    className="site-header__btn site-header__btn--start site-nav-action-trigger"
                    href={appLinks.signup}
                    {...appLinkProps}
                  >
                    {t("startFree.label")}
                  </a>
                </>
              )}
            </div>

            <button
              type="button"
              ref={mobileToggleRef}
              className="site-header__mobile-toggle"
              aria-controls="site-mobile-navigation"
              aria-label={mobileMenuOpen ? t("closeMenu") : t("openMenu")}
              aria-expanded={mobileMenuOpen}
              onClick={() => setMobileMenuOpen((o) => !o)}
            >
              {mobileMenuOpen ? (
                <X className="h-6 w-6" />
              ) : (
                <Menu className="h-6 w-6" />
              )}
            </button>
            </div>

            {languageSwitcher}
          </div>
        </div>

        {activeMegaMenu ? (
          <div
            className={`site-nav-panel-position absolute left-1/2 top-full z-[85] -translate-x-1/2 pt-3 ${
              activeMegaMenuIsMarketplace
                ? "w-[min(calc(100vw-2rem),32rem)]"
                : megaMenuWidthClassName(activeMegaMenu.sections.length)
            }`}
            onMouseEnter={() => openDropdown(`mega:${activeMegaMenu.id}`)}
            onMouseLeave={scheduleDropdownClose}
          >
            <div
              className="site-nav-panel"
              id={`nav-dropdown-${activeMegaMenu.id}`}
              role="region"
              aria-label={activeMegaMenu.label}
            >
              <div className="site-nav-panel__solutions">
                <Link href={internalHref(activeMegaMenu.footerCta?.href ?? activeMegaMenu.href)} className="site-nav-panel__intro" onClick={closeDropdown}>
                  <span className="site-nav-panel__eyebrow">{activeMegaMenu.label}</span>
                  <h2>{activeMegaMenu.introTitle}</h2>
                  <p>{activeMegaMenu.introBody}</p>
                  <span className="site-nav-panel__intro-cta">{activeMegaMenu.footerCta?.label}</span>
                </Link>
                <div className="site-nav-panel__columns" style={{gridTemplateColumns:`repeat(${activeMegaMenu.sections.length}, minmax(0, 1fr))`}}>
                  {activeMegaMenu.sections.map(section => <section key={section.id}>
                    <h3 className="site-nav-panel__heading">{section.title}</h3>
                    <div className="site-nav-panel__links">{section.items.map(item => renderMegaMenuLink(item, "site-nav-panel__link", `${section.id}:${item.href}`))}</div>
                  </section>)}
                </div>
              </div>
            </div>
          </div>
        ) : null}

        {mobileMenuOpen ? (
          <div ref={mobilePanelRef} id="site-mobile-navigation" className="site-header__mobile-panel" role="region" aria-label={t("openMenu")}>
            <div className="space-y-3 px-4 py-4">
              {chromeSession.isAuthenticated ? (
                <div className="space-y-1 border-t border-gray-100 pt-3">
                  <div className="flex items-center gap-3 rounded-lg bg-slate-50 px-3 py-2">
                    <span className="relative grid h-10 w-10 shrink-0 place-items-center">
                      <span
                        className="grid h-9 w-9 place-items-center overflow-hidden rounded-full text-sm font-bold text-slate-800"
                        style={!chromeSession.avatarUrl ? { background: fallbackAvatarColor } : undefined}
                        aria-hidden="true"
                      >
                        {chromeSession.avatarUrl ? (
                          // eslint-disable-next-line @next/next/no-img-element
                          <img className="h-full w-full object-cover" src={chromeSession.avatarUrl} alt="" />
                        ) : (
                          fallbackInitials
                        )}
                      </span>
                      {chromeSession.membershipType && membershipLabel ? (
                        <MembershipCrest
                          ariaLabel={`Active membership: ${membershipLabel}`}
                          className="pointer-events-none absolute -right-1 -top-1 z-10 rounded-full border-2 border-white bg-white drop-shadow-sm"
                          membershipType={chromeSession.membershipType}
                          size="avatar-small"
                        />
                      ) : null}
                    </span>
                    <span className="grid min-w-0 leading-tight">
                      <strong className="truncate text-sm text-slate-950">{displayName}</strong>
                      {membershipLabel ? (
                        <small className="mt-0.5 truncate text-xs font-semibold text-slate-500">
                          {membershipLabel}
                        </small>
                      ) : null}
                    </span>
                  </div>
                  {accountMenuItems.map(({ href, icon: Icon, label }) => (
                    <a
                      key={href}
                      href={href}
                      {...appLinkProps}
                      className="flex items-center gap-3 rounded-lg px-3 py-2 text-left font-semibold text-gray-700 hover:bg-gray-50"
                      onClick={closeMobile}
                    >
                      <Icon aria-hidden="true" className="h-4 w-4 text-slate-500" />
                      {label}
                    </a>
                  ))}
                  <button
                    type="button"
                    className="flex w-full items-center gap-3 rounded-lg px-3 py-2 text-left font-semibold text-red-600 hover:bg-red-50"
                    onClick={() => onLogout()}
                  >
                    <LogOut aria-hidden="true" className="h-4 w-4" />
                    {t("accountLogout")}
                  </button>
                </div>
              ) : (
                <div className="space-y-3 border-t border-gray-100 pt-3">
                  <a
                    className="flex min-h-11 items-center justify-center rounded-md border border-primary px-4 text-sm font-bold text-primary hover:bg-primary hover:text-white"
                    href={appLinks.login}
                    {...appLinkProps}
                    onClick={closeMobile}
                  >
                    {t("login")}
                  </a>
                  <a
                    className="flex min-h-11 items-center justify-center rounded-md bg-accent px-4 text-sm font-bold text-white hover:bg-accent/90"
                    href={appLinks.signup}
                    {...appLinkProps}
                    onClick={closeMobile}
                  >
                    {t("startFree.label")}
                  </a>
                </div>
              )}
              <div className="site-nav-mobile-utilities">
                {marketplaceMenus.map((item) => {
                  const className = `flex items-center justify-between rounded-md px-3 py-3 text-sm font-bold text-slate-800 hover:bg-slate-50 hover:text-primary ${item.id === "find-pilots-jobs" ? "site-nav-discovery-link" : ""} ${item.subtitle ? "site-nav-featured-link" : ""}`;
                  if (Boolean(marketplaceHref(item))) {
                    return (
                      <a
                        key={`mobile-marketplace:${item.id}`}
                        href={marketplaceHref(item) || item.href}
                        target={appLinksOpenInNewTab || item.openInNewTab ? "_blank" : undefined}
                        className={className}
                        rel="noopener noreferrer"
                        onClick={() => {
                          track("public_navigation_click", {
                            destination: item.href,
                            label: item.label,
                          });
                          closeMobile();
                        }}
                      >
                        {renderMarketplaceLabel(item)}
                      </a>
                    );
                  }
                  return renderInternalMenuLink(
                    item.href,
                    item.label,
                    className,
                    closeMobile,
                    `mobile-marketplace:${item.id}`,
                  );
                })}
              </div>
              {megaMenus.map((item) => {
                if (item.directLink) return renderInternalMenuLink(item.href, item.label, "block rounded-lg border border-slate-200 px-3 py-3 font-bold text-slate-800 focus-visible:outline focus-visible:outline-2 focus-visible:outline-accent", closeMobile, item.id);
                const expanded = mobileExpandedId === item.id;
                return (
                  <div className="rounded-lg border border-slate-200" key={item.id}>
                    <button
                      type="button"
                      className="flex w-full items-center justify-between gap-3 px-3 py-3 text-left font-bold text-slate-800"
                      aria-expanded={expanded}
                      onClick={() => setMobileExpandedId(expanded ? null : item.id)}
                    >
                      {item.label}
                      <ChevronDown
                        aria-hidden="true"
                        className={`h-4 w-4 shrink-0 transition ${expanded ? "rotate-180" : ""}`}
                      />
                    </button>
                    {expanded ? (
                      <div className="border-t border-slate-100 px-3 py-3">
                        {renderInternalMenuLink(
                          item.href,
                          item.footerCta?.label ?? item.label,
                          "mb-3 block rounded-md bg-primary px-3 py-3 text-sm font-semibold text-white",
                          closeMobile,
                        )}
                        <div className="space-y-4">
                          {item.sections.map((section, sectionIndex) => (
                            <section key={`${section.id}:${sectionIndex}`}>
                              {section.title ? (
                                <h3 className="site-nav-panel__heading">{section.title}</h3>
                              ) : null}
                              <div className="grid gap-1">
                                {section.items.map((sectionItem) => renderMegaMenuLink(
                                  sectionItem,
                                  "site-nav-panel__link",
                                  `${section.title}:${sectionItem.label}`,
                                  closeMobile,
                                ))}
                              </div>
                            </section>
                          ))}
                        </div>
                      </div>
                    ) : null}
                  </div>
                );
              })}




            </div>
          </div>
        ) : null}
      </nav>
    </>
  );
}
